import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { BaseComponent } from '../../../core/base-component';
import { MtnMap } from '../../mtn-map';
import { RingMilesFieldComponent } from '../../../core/form-controls/controls/ring-miles-field/ring-miles-field.component';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { MapEvent, MapEventType } from '../../map-events';
import { MapSelectionMode } from '../../map-selection-mode.enum';
import { AnalyticsEventType } from '../../../google-analytics/analytics-event-type';
import Timeout = NodeJS.Timeout;
import Marker = google.maps.Marker;
import { HitTrackingService } from '../../../core/hit-tracking/hit-tracking.service';
import { USER_TRACKING_ACTIONS, USER_TRACKING_SOURCES } from 'src/app/core/constants/user-tracking.constants';

@Component({
  standalone: false,
  selector: 'mtn-map-selection-tools-assembly',
  templateUrl: './map-selection-tools-assembly.component.html',
  styleUrls: ['./map-selection-tools-assembly.component.scss']
})
export class MapSelectionToolsAssemblyComponent extends BaseComponent implements OnInit {

  @Input()
  map: MtnMap;

  @Input()
  disablePNC: () => void;

  @ViewChild("radiusControl")
  radiusControl: RingMilesFieldComponent;

  AnalyticsEventType = AnalyticsEventType;
  isHidden = true;
  isHiding = false;
  isShowing = false;
  isShown = false;
  MapSelectionMode = MapSelectionMode;
  ringForm = new UntypedFormGroup({
    ringMiles: new UntypedFormControl(3, [Validators.min(1), Validators.max(100), Validators.required])
  });

  private _activeTimeout: Timeout;

  constructor(private hitTrackingService: HitTrackingService) {
    super();
  }

  ngOnInit() {
    this.subscribeToMapEvents();
  }

  getSelectionIcon(): IconProp {
    switch (this.map.selectionMode) {
      case MapSelectionMode.LASSO:
        return 'lasso';
      case MapSelectionMode.MULTI_SELECT:
        return 'hand-pointer';
      case MapSelectionMode.RING:
        return ['far', 'circle'];
      default:
        return 'mouse-pointer';
    }
  }

  selectAll(): void {
    this.disablePNC?.();
    this.map._isSelectAll = "SELECT_ALL";
    this.map.selectAll(this.map.markers.map((marker: Marker) => marker['id']));
    this.map.setSelectionMode(MapSelectionMode.MULTI_SELECT);
    this.closeSlide();

    this.trackUserAction('MAP_SELECT_ALL', 'User clicked Select All');
  }

  setSelectionMode(mode: MapSelectionMode): void {
    this.disablePNC?.();
    this.map.setSelectionMode(mode, this.ringForm.get('ringMiles'));
    this.map._isSelectAll = null;

    switch (mode) {
      case MapSelectionMode.MULTI_SELECT:
        this.trackUserAction('MAP_MULTI_SELECT', 'User activated Multi-Select mode');
        break;
      case MapSelectionMode.LASSO:
        this.trackUserAction('MAP_LASSO_SELECT', 'User activated Lasso mode');
        break;
      case MapSelectionMode.RING:
        this.trackUserAction('MAP_RING_SELECT', 'User activated Ring mode', {
          radius: this.ringForm.get('ringMiles')?.value
        });
        setTimeout(() => this.radiusControl.radiusInput.nativeElement.focus(), 100);
        break;
      case null:
        this.trackUserAction('MAP_CANCEL_SELECTION', 'User cancelled selection mode');
        this.map.select(null);
        break;
    }

    if (mode !== MapSelectionMode.RING) {
      this.closeSlide();
    }
  }

  toggleSelectionTools(): void {
    this.disablePNC?.();
    this.map.disableDrawing();
    this.toggleSlide();

    this.trackUserAction('MAP_TOGGLE_SELECTION_TOOLS', 'User toggled map selection tools panel');
  }

  closeSlide(): void {
    this.stopActiveTransition();

    this.isShown = false;
    this.isHiding = true;

    this._activeTimeout = setTimeout(() => {
      this.isHiding = false;
      this.isHidden = true;
    }, 600);
  }

  openSlide(): void {
    this.stopActiveTransition();

    this.isHidden = false;
    this.isShowing = true;

    this._activeTimeout = setTimeout(() => {
      this.isShowing = false;
      this.isShown = true;
    }, 100);
  }

  toggleSlide(): void {
    if (this.isShowing || this.isShown) {
      this.closeSlide();
    } else {
      this.openSlide();
    }
  }

  private stopActiveTransition(): void {
    if (this._activeTimeout) {
      clearTimeout(this._activeTimeout);
      this._activeTimeout = null;
    }
  }

  private subscribeToMapEvents(): void {
    this.addSubscription(
      this.map.events
        .subscribe((event: MapEvent<any>) => {
          if ((this.isShowing || this.isShown) && event.type === MapEventType.CLICK) {
            this.closeSlide();
          }
        })
    );
  }

  private trackUserAction(action: string, description: string, data?: any): void {
    console.log(action);
    this.hitTrackingService.hitTheUserTracking(
      USER_TRACKING_SOURCES.MAP_SELECTION_TOOLS,
      data ?? {},
      USER_TRACKING_ACTIONS.SELECTION_ACTION,
      description
    ).subscribe();
  }
}
